<template>
  <div class="export">
    <div
      v-for="collectionGroup in collectionItems"
      :key="`project-${collectionGroup.name}`"
      class="export__collection"
    >
      <div
        v-for="(itemGroup, itemGroupIndex) in collectionItemsStylesSplited[
          `${collectionGroup.projectId}-${collectionGroup.name}`
        ]"
        :key="`item-${collectionGroup.name}-${itemGroupIndex}`"
        ref="collectionsGroup"
        class="export__collection-group"
      >
        <div class="export__collection-project-name">
          {{ collectionGroup.name }}
        </div>
        <div class="export__collection-style-container">
          <div
            v-for="(item, itemIndex) in itemGroup"
            :key="`item-${getStyleId(item)}-${itemIndex}`"
            class="export__collection-style"
          >
            <StylePreview
              rounded
              :style-id="getStyleId(item)"
              :image-resolution="imageResolution"
            />
          </div>
        </div>
      </div>
    </div>

    <StitchDialog
      visible
      center
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      title="Export collections"
    >
      <div class="export__progress-body">
        <ElProgress
          class="progress"
          type="dashboard"
          :percentage="progressPercentage"
          :status="progressPercentage === 100 ? 'success' : null"
        />

        <h2 v-if="progressPercentage === 100">
          Your collections are ready...<br>
          Your PDF will be <br>
          automatically downloaded.
        </h2>
        <div v-else>
          <h2>
            Preparing the collection... this may take several minutes
          </h2>
          <h2 v-if="collectionsGroupsConvertedCount > 0">
            Exported {{ collectionsGroupsConvertedCount }} of
            {{ $refs.collectionsGroup.length }}.
          </h2>
        </div>
      </div>
      <div
        v-if="progressPercentage === 100"
        slot="footer"
        class="export__progress-footer"
      >
        <router-link :to="`/projects/`">
          <ElButton type="text">
            Go back to projects
          </ElButton>
        </router-link>
      </div>
    </StitchDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { Projects } from '@/mixins/projects'
import { S3Trim } from '@/mixins/s3Trim.js'
import { Workspace } from '@/mixins/workspace'

import { imageResolutions } from '@/constants/image'
import features from '@/constants/features'

import Utils from '@/services/utils'

import StylePreview from '@/components/project/StylePreview.vue'

import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  name: 'ExportCollection',

  components: {
    StylePreview
  },

  mixins: [Projects, S3Trim, Workspace],

  data: () => ({
    isGeneratingPDF: false,
    collectionsLoadedCount: 0,
    collectionsGroupsConvertedCount: 0,
    pdf: null,
    isModeMCR: false
  }),

  computed: {
    ...mapGetters(['getStyleById', 'getOptions']),

    /**
     * @returns {number}
     */
    progressPercentage () {
      if (this.collectionItems.length > 0) {
        let totalGroups = 0

        Object.values(this.collectionItemsStylesSplited).forEach(
          stylesGroups => {
            const groups = stylesGroups.map(styles => styles.length)

            totalGroups += groups.length
          }
        )

        return Math.round(
          ((this.collectionsLoadedCount +
            this.collectionsGroupsConvertedCount) /
            (this.collectionItems.length + totalGroups)) *
            100
        )
      } else {
        return 0
      }
    },

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.STYLE
    },

    /**
     * @returns {object}
     */
    collectionItemsStylesSplited () {
      const styleMap = {}
      this.collectionItems.forEach(collection => {
        const propertyName = `${collection.projectId}-${collection.name}`

        styleMap[propertyName] = []
        let sectionItem = []

        collection.items.forEach((style, index) => {
          sectionItem.push(style)

          if (index !== 0 && index % 5 === 0) {
            styleMap[propertyName].push(sectionItem)
            sectionItem = []
          } else if (index === collection.items.length - 1) {
            styleMap[propertyName].push(sectionItem)
          }
        })
      })

      return styleMap
    },

    /**
     * @returns {Array}
     */
    collectionItems () {
      return this.optionsFiltered.reduce((itemsGrouped, option) => {
        const projectId = option.projectId || option.project.id
        const item = this.displayOptions
          ? option
          : this.getStyleById(option.style_id)
        const collectionGroupKey = this.getCollectionGroupKey(
          item,
          this.displayOptions,
          this.isModeMCR
        )

        let collectionGroup = itemsGrouped.find(
          group => group.name === collectionGroupKey
        )

        if (!collectionGroup) {
          collectionGroup = {
            projectId,
            name: collectionGroupKey,
            items: []
          }

          itemsGrouped.push(collectionGroup)
        }

        collectionGroup.items = [...new Set(collectionGroup.items.concat(item))]

        return itemsGrouped
      }, [])
    },

    /**
     * @returns {Array}
     */
    optionsFiltered () {
      return this.getOptions.filter(option => {
        return (
          Utils.isUserCreatorInGroup(this.user, this.selectedGroupId) ||
          (option.project && option.project.visible_to_sales)
        )
      })
    },

    /**
     * @returns {boolean}
     */
    canUserAccessFeatureCollectionOverviewByFashionability () {
      if (!this.user) {
        return false
      }

      return this.$featureFlagsManager.canAccessFeature(
        features.COLLECTION_OVERVIEW_BY_FASHIONABILITY,
        {
          user: this.user
        }
      )
    }
  },

  async created () {
    this.checkRouteParameters()

    if (this.canUserAccessFeatureCollectionOverviewByFashionability) {
      this.isModeMCR = true
    }

    await this.loadCollections()
  },

  methods: {
    ...mapActions([
      'fetchOptionsPaginated',
      'fetchStylesPaginated',
      'updateSelectedGroupId',
      'updateSelectedSeasonId'
    ]),

    /**
     */
    async loadCollections () {
      const payload = {
        groupId: this.selectedGroupId,
        seasonId: this.selectedSeasonId,
        filterQuery: {},
        searchQuery: '',
        limit: 999999,
        page: 1
      }

      if (this.isModeMCR) {
        payload.order = 'MCR'
      }

      await this.fetchStylesPaginated(payload)

      this.collectionItems.forEach(collection => {
        const images = collection.items.map(item => {
          return item.options.map(option => option.assets_file)
        })
        this.loadImages(images.flat().filter(image => image !== null))
      })
    },

    /**
     * @param {string[]} imageUrls
     */
    loadImages (imageUrls) {
      const totalImages = imageUrls.length
      let imageCount = 0

      imageUrls.forEach(imageUrl => {
        const image = new Image()
        image.crossOrigin = 'use-credentials'
        image.onload = () => {
          imageCount++

          if (imageCount === totalImages) {
            this.incrementCollectionsLoadedCount()
          }
        }

        image.src = this.s3Trim(imageUrl, this.imageResolution)
      })
    },

    /**
     */
    incrementCollectionsLoadedCount () {
      this.collectionsLoadedCount++

      if (this.collectionsLoadedCount === this.collectionItems.length) {
        this.createExportPDF()
      }
    },

    /**
     */
    async createExportPDF () {
      await this.convertImagesToBase64()

      this.pdfDoc = new JsPDF({
        format: 'a4',
        orientation: 'landscape'
      })

      for (let index = 0; index < this.$refs.collectionsGroup.length; index++) {
        const collectionSnapshotCanvas = await this.createCollectionSnapshot(
          this.$refs.collectionsGroup[index]
        )
        const imageWidth =
          (collectionSnapshotCanvas.width / collectionSnapshotCanvas.height) *
          this.pdfDoc.internal.pageSize.getHeight()
        const imageMarginLeft =
          (this.pdfDoc.internal.pageSize.getWidth() - imageWidth) / 2

        this.pdfDoc.addImage(
          collectionSnapshotCanvas,
          'PNG',
          imageMarginLeft,
          0,
          imageWidth,
          this.pdfDoc.internal.pageSize.getHeight()
        )

        if (index !== this.$refs.collectionsGroup.length - 1) {
          this.pdfDoc.addPage()
        }

        this.collectionsGroupsConvertedCount++
      }

      this.downloadExportPDF()
    },

    /**
     */
    convertImagesToBase64 () {
      return new Promise((resolve, reject) => {
        const images = document.querySelectorAll('img')
        let imageDone = 0
        const imageCount = images.length
        const canvas = document.createElement('canvas')

        // @TODO
        // TODO for some reason locally,
        // Babel caches the whole view wich result to an error like this:
        // Uncaught (in promise) DOMException: Failed to execute 'toDataURL'
        //  on 'HTMLCanvasElement': Tainted canvases may not be exported.
        // webpack-internal:///./node_modules/cache-loader/dist/cjs.js?
        // !./node_modules/babel-loader/lib/index.js!./node_modules/cache-loader/dist/cjs.js
        // ?!./node_modules/vue-loader/lib/index.js?!./src/views/project/ExportBoards.vue?vue&type=script&lang=js&

        const sizeMultiplier = 3

        for (const image of images) {
          const widthMultiplied = image.width * sizeMultiplier
          const heightMultiplied = image.height * sizeMultiplier
          canvas.width = widthMultiplied
          canvas.height = heightMultiplied
          const context = canvas.getContext('2d')
          context.drawImage(image, 0, 0, widthMultiplied, heightMultiplied)

          image.onload = () => {
            imageDone++

            if (imageDone === imageCount) {
              resolve()
            }
          }

          image.src = canvas.toDataURL()
        }
      })
    },

    /**
     * @param   {Element} collectionReference
     *
     * @returns {Promise}
     */
    createCollectionSnapshot (collectionReference) {
      return html2canvas(collectionReference, {
        scale: 2
      })
    },

    /**
     */
    downloadExportPDF () {
      const downloadDate = new Date()
      const formattedDate =
        downloadDate.getDate() +
        '-' +
        (downloadDate.getMonth() + 1) +
        '-' +
        downloadDate.getFullYear()

      this.pdfDoc.save(`Collection overview - ${formattedDate}.pdf`)
    },

    /**
     */
    checkRouteParameters () {
      const groupId = this.$route.query.group_id
      const seasonId = this.$route.query.season_id

      if (groupId && seasonId) {
        this.updateSelectedGroupId({
          groupId: Number.parseInt(groupId)
        })
        this.updateSelectedSeasonId({
          seasonId: Number.parseInt(seasonId)
        })
      } else {
        this.$router.push('/')
      }
    },

    /**
     * @param   {object}  item
     * @param   {boolean} isItemOption
     * @param   {boolean} isModeMCR
     *
     * @returns {string}
     */
    getCollectionGroupKey (item, isItemOption, isModeMCR) {
      if (isModeMCR) {
        const fashionabilityDefault = 'Other'
        const fashionability = isItemOption
          ? this.getStyleFashionability(item.style)
          : this.getStyleFashionability(item)

        return fashionability || fashionabilityDefault
      } else {
        return item.project.name
      }
    },

    /**
     * @param   {object}        style
     *
     * @returns {string | null}
     */
    getStyleFashionability (style) {
      if (!style) {
        return null
      }

      return style.fashionability && style.fashionability.name
    },

    /**
     * @param   {object} item
     *
     * @returns {number}
     */
    getStyleId (item) {
      if (this.displayOptions) {
        return item.style_id
      } else {
        return item.id
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$a4-margin-left: 130px;
$a4-landscape-width: 29.7cm;
$a4-landscape-height: 21cm;

.export__collection {
  margin-bottom: $spacer-unit-x2;

  /deep/ .el-loading-mask {
    display: none;
  }
}

.export__collection-project-name {
  margin-bottom: $spacer-unit-x2;
  color: $grey;
  font-size: $font-size-extra-large;
  text-align: center;
}

.export__collection-group {
  width: $a4-landscape-width;
  height: $a4-landscape-height;
  margin-bottom: $spacer-unit-x2;
  padding: $spacer-unit-x4;
  vertical-align: middle;
  background-color: $grey-extra-light;
}

.export__collection-style-container {
  display: grid;
  grid-auto-rows: $min-width-aside-large;
  grid-gap: $gutter;
  grid-template-columns: repeat(auto-fit, minmax($min-width-aside-large, 1fr));
}

.export__progress-body,
.export__progress-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.export__progress-body {
  width: 100%;

  .progress {
    margin-bottom: $spacer-unit-x2;
  }
}
</style>

<style lang="scss">
@media print {
  .el-popup-parent--hidden {
    .el-dialog__wrapper,
    .v-modal {
      display: none;
    }
  }
}
</style>
